import { createReducer } from '@reduxjs/toolkit';

import { toastrDismiss, toastrShowError, toastrShowInfo, toastrShowSuccess, toastrUpdateIsShow, toastrUpdateMessage, toastrUpdateType } from './toastrActions.js';

// Toasts stack instead of overwriting each other, but a burst (a failed batch save, a few copies in
// a row) must not cover the screen — only the newest few stay.
const MAX_VISIBLE = 3;

const initialState = {
  items: [],
  // Legacy two-step API (`toastrUpdateMessage` + `toastrUpdateIsShow`), still used by a few block
  // editors: the text waits here until `isShow` turns it into a toast.
  pendingMessage: 'Appearance saved',
  seq: 0,
};

// Callers pass whatever they have on hand — `error.message`, a parsed response body, sometimes
// nothing. Normalizing here keeps the component free of "message may be an object" guards.
const toText = (message, fallback) => {
  if (typeof message === 'string' && message.trim()) return message;
  if (message && typeof message === 'object') return message.Message ?? message.message ?? fallback;
  return fallback;
};

const push = (state, message, type) => {
  const seq = state.seq + 1;

  return { ...state, seq, items: [...state.items, { id: seq, message, type }].slice(-MAX_VISIBLE) };
};

const patchLast = (state, patch) => ({
  ...state,
  items: state.items.map((item, index) => (index === state.items.length - 1 ? { ...item, ...patch } : item)),
});

const toastr = createReducer(initialState, (builder) => {
  builder
    .addCase(toastrShowSuccess, (state, { payload }) => push(state, toText(payload, 'Saved'), 'success'))
    .addCase(toastrShowError, (state, { payload }) => push(state, toText(payload, 'Something went wrong'), 'error'))
    .addCase(toastrShowInfo, (state, { payload }) => push(state, toText(payload, ''), 'info'))
    .addCase(toastrDismiss, (state, { payload: id }) => ({ ...state, items: state.items.filter((item) => item.id !== id) }))
    .addCase(toastrUpdateIsShow, (state, { payload: isShow }) => (isShow ? push(state, state.pendingMessage, 'success') : { ...state, items: [] }))
    // The legacy call sites dispatch `isShow` first and the text right after, so a message that
    // arrives while a toast is up belongs to that toast.
    .addCase(toastrUpdateMessage, (state, { payload: message }) =>
      state.items.length ? patchLast(state, { message: toText(message, 'Saved') }) : { ...state, pendingMessage: toText(message, 'Saved') },
    )
    .addCase(toastrUpdateType, (state, { payload: type }) => (state.items.length ? patchLast(state, { type }) : state));
});

export default toastr;
